/** Presentation helpers. Pure functions, kept out of the domain layer as they are purely about display. */

import { isToolpath, kindOf } from './domain/modelFile.js';
import type { ModelKind, ModelStats, Vec3 } from './domain/types.js';

const BYTE_UNITS = ['B', 'kB', 'MB', 'GB'] as const;

export function formatBytes(bytes: number): string {
  let value = bytes;
  let unit = 0;

  while (value >= 1000 && unit < BYTE_UNITS.length - 1) {
    value /= 1000;
    unit += 1;
  }

  const decimals = unit === 0 || value >= 100 ? 0 : 1;
  return `${value.toFixed(decimals)} ${BYTE_UNITS[unit] ?? 'B'}`;
}

export function formatCount(count: number): string {
  return count.toLocaleString();
}

/**
 * Render a model's extent as a human-readable measurement.
 *
 * Switches to metres past a metre, because "1850 mm" is a number you have to convert in
 * your head and "1.85 m" is a thing you can picture.
 */
export function formatDimensions(sizeMm: Vec3): string {
  const largest = Math.max(...sizeMm);

  if (largest >= 1000) {
    const parts = sizeMm.map((mm) => (mm / 1000).toFixed(2));
    return `${parts.join(' × ')} m`;
  }

  const decimals = largest < 10 ? 2 : 1;
  const parts = sizeMm.map((mm) => trimZeros(mm.toFixed(decimals)));
  return `${parts.join(' × ')} mm`;
}

/**
 * What a model is made of, in the words that fit what it actually is.
 *
 * A toolpath has no triangles, so saying "0 triangles" about one would be true and
 * useless. Both libraries and the in-VR menu ask this rather than reaching for a count
 * themselves, so a G-code file is described the same way everywhere.
 */
export function describeContents(stats: ModelStats): string {
  if (kindOf(stats) !== 'gcode') {
    return `${formatCount(stats.triangleCount)} triangles`;
  }

  const moves = `${formatCount(stats.segmentCount ?? 0)} moves`;
  const layers = stats.layerCount;

  // Records written by an older version have no layer count. Saying nothing about layers
  // beats saying "0 layers" about a file that plainly has some.
  if (layers === undefined || layers === 0) return moves;
  return `${moves} · ${formatCount(layers)} ${layers === 1 ? 'layer' : 'layers'}`;
}

/**
 * What to say about the toolpaths in a library, or nothing if there are none.
 *
 * G-code is the one kind here that is not a description of a shape. A mesh file says where
 * the surfaces are; a print file says where the nozzle went, and the object has to be
 * worked back out of that. It usually comes out well, but it is a reconstruction, and
 * somebody looking at a print that does not match the model they sliced deserves to know
 * that before concluding their slicer is at fault.
 *
 * Counted across the whole library rather than asked about the model on display, because
 * the point is to explain the ones sitting in the list waiting to be looked at.
 */
export function describeToolpathCaveat(
  models: readonly { readonly kind?: ModelKind }[],
): string | undefined {
  const count = models.filter((model) => isToolpath(model)).length;
  if (count === 0) return undefined;

  const which =
    count === 1
      ? 'One of your models is G-code'
      : `${formatCount(count)} of your models are G-code`;

  return `${which}. A toolpath says how a thing was printed rather than what shape it is, so what you see is the object rebuilt from the path — surfaces can come out rough, and anything that was never printed as an outline is not there at all.`;
}

function trimZeros(text: string): string {
  return text.includes('.') ? text.replace(/\.?0+$/, '') : text;
}

const TIME_OF_DAY = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
});

const CALENDAR_DAY = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

/**
 * Render a moment the way a sign-in history wants to be read.
 *
 * "Yesterday at 21:14" is something a person can check against their memory;
 * "2026-08-14T21:14:03Z" is not, and that checking is the entire point of showing the
 * history at all. Anything older than yesterday gets its date, because by then the
 * weekday alone is ambiguous.
 *
 * Rendered in the viewer's own locale and timezone, which is what makes it comparable to
 * their recollection — the server stores and sends UTC.
 */
export function describeMoment(iso: string, now: Date = new Date()): string {
  const moment = new Date(iso);
  // An unparseable timestamp means the server sent something unexpected. Showing the raw
  // value is more use to whoever has to work out why than the word "Invalid" would be.
  if (Number.isNaN(moment.getTime())) return iso;

  const time = TIME_OF_DAY.format(moment);
  const days = calendarDaysBetween(moment, now);

  if (days === 0) return `Today at ${time}`;
  if (days === 1) return `Yesterday at ${time}`;
  return `${CALENDAR_DAY.format(moment)} at ${time}`;
}

/**
 * Whole calendar days from `moment` to `now`, in local time.
 *
 * Counted by calendar day rather than by elapsed hours, because "yesterday" means the
 * previous date and not twenty-four hours ago — at nine in the morning, something at
 * eleven the previous night was yesterday, despite being ten hours back.
 */
function calendarDaysBetween(moment: Date, now: Date): number {
  const startOfDay = (date: Date): number =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  return Math.round((startOfDay(now) - startOfDay(moment)) / millisecondsPerDay);
}

/**
 * A model's size in the room as a percentage, or nothing when that is its true size.
 *
 * Nothing rather than "100%" for the ordinary case: a figure shown against every model is
 * a figure nobody reads, and what makes this one worth reading is that it is unusual.
 *
 * Whole percentages, because no headset user is counting tenths — except right beside
 * 100%, where rounding would report a model as true size when it is not. The one place
 * this must not round is the one place it would be claiming the model can be trusted, so
 * there it gains a decimal instead.
 */
export function describeRoomScale(ratio: number): string | undefined {
  if (ratio === 1) return undefined;

  const percent = ratio * 100;
  const rounded = Math.round(percent);

  return rounded === 100 ? `${percent.toFixed(1)}%` : `${rounded.toString()}%`;
}
